// The call surfaces that live ABOVE the router (Layer B3): the incoming-call
// sheet and the outgoing "calling…" card. Mounted once app-wide, so a call
// reaches you on any page.
//
// Ring audio is owned here and stopped in the effect cleanup, so a resolved
// call always goes silent — a ringtone that outlives its call is the worst bug
// this feature could ship.

import { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { api } from '../api';
import { peerSummary } from '../lib/calls';
import { onDesktopOpenCall } from '../desktop';
import { startRingtone } from '../sounds';
import { callsStore, setCallOutcome, setIncomingCall, useCallsStore } from '../store/callsStore';
import { presenceStore } from '../store/presenceStore';
import { pushToast } from '../store/callStore';
import { Avatar } from '../ui/Avatar';
import { IconMicrophone, IconLeave } from '../ui/icons';

export function CallSurfaces() {
  return (
    <>
      <IncomingCall />
      <OutgoingCall />
    </>
  );
}

const IncomingCall = memo(function IncomingCall() {
  const call = useCallsStore((s) => s.incoming);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  // Ring while the sheet is up; silence on any resolution (unmount). The org's
  // ringtone is read from the store at ring time rather than subscribed to, so
  // a presence update can never restart a ring in progress (§9.3).
  useEffect(() => {
    if (!call) return;
    const stop = startRingtone(presenceStore.getState().ringtone);
    return stop;
  }, [call]);

  // Desktop: a native-notification click or a brizina://call/<id> deep link asks
  // us to OPEN a specific call. If that call is the one already ringing, the
  // incoming sheet is up in the now-focused window — leave Accept/Decline to the
  // user. Otherwise (a deep link, or a call being rejoined with no sheet) route
  // straight to it; /call/:id joins idempotently server-side. No-op in a browser.
  useEffect(
    () =>
      onDesktopOpenCall((callId) => {
        if (callsStore.getState().incoming?.id === callId) return;
        navigate(`/call/${callId}`);
      }),
    [navigate],
  );

  const accept = useCallback(() => {
    if (!call) return;
    setBusy(true);
    api
      .acceptCall(call.id)
      .then((r) => {
        // /call/:id fetches its own grant server-side (idempotent join) — no
        // fragile client-side handoff, and calls never touch the meeting route.
        setIncomingCall(null);
        navigate(`/call/${r.call.id}`);
      })
      .catch((e: unknown) => {
        pushToast(e instanceof Error ? e.message : 'Could not join the call', 'error');
        setIncomingCall(null);
      })
      .finally(() => setBusy(false));
  }, [call, navigate]);

  const decline = useCallback(() => {
    if (!call) return;
    setIncomingCall(null);
    api.declineCall(call.id).catch(() => undefined);
  }, [call]);

  if (!call) return null;

  return (
    <div className="bz-incall-sheet" role="dialog" aria-label="Incoming call">
      <div className="bz-incall-who">
        <Avatar
          name={call.callerName || 'Caller'}
          identity={call.callerId}
          size={56}
          photoUrl={call.callerAvatar}
        />
        <div className="bz-incall-text">
          <div className="bz-incall-name">{call.callerName || 'Someone'}</div>
          <div className="bz-incall-sub">
            {call.kind === 'group'
              ? `Group call · with ${peerSummary(call.peers.map((p) => p.name))}`
              : `Incoming ${call.media === 'video' ? 'video' : 'audio'} call…`}
          </div>
        </div>
      </div>
      <div className="bz-incall-actions">
        <button
          type="button"
          className="bz-incall-decline"
          onClick={decline}
          aria-label="Decline call"
        >
          <IconLeave size={20} />
        </button>
        <button
          type="button"
          className="bz-incall-accept"
          onClick={accept}
          disabled={busy}
          aria-label="Accept call"
        >
          <IconMicrophone size={20} />
        </button>
      </div>
    </div>
  );
});

/** Transient outcome strip ("Call declined" / "No answer") — the caller now
 * lives on /call/:id, so this never shows a duplicate "Calling…" surface. */
const OutgoingCall = memo(function OutgoingCall() {
  const outcome = useCallsStore((s) => s.outcome);

  // Shows briefly, then clears itself.
  useEffect(() => {
    if (!outcome) return;
    const t = window.setTimeout(() => setCallOutcome(null), 2600);
    return () => window.clearTimeout(t);
  }, [outcome]);

  if (!outcome) return null;

  return (
    <div className="bz-outcall" role="status">
      <span className="bz-outcall-text">{outcome}</span>
    </div>
  );
});
